import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { appendToRmaBaseUrl } from '@rma/generic/ng/util';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { PublicProfileContextService } from '@rma/public/accounts/user';
import { QrCodeService } from '@rma/public/generic/api/qr-code';
import { RmaModalService } from '@rma/public/generic/ui/modal';
import { FeatureFlags, FeatureFlagService } from '@rma/public/generic/util/feature-flags';
import { applyWithDialogDefaults } from '@rma/public/generic/util/material';
import { BehaviorSubject, filter, map, Observable, shareReplay, switchMap } from 'rxjs';
import { MarketReportApiService } from '../data-access/market-report-api/market-report-api.service';
import { MarketReport } from '../domain';
import { MarketReportCommunicationType } from '../domain/market-report-communication-request';
import { MarketReportCommunicationFormComponent } from './feat-market-report-communication-form/market-report-communication-form.component';
import { QrCodeDownloadComponent } from './feat-qr-code-download/qr-code-download.component';

@UntilDestroy()
@Component({
  selector: 'rma-market-report-publish',
  templateUrl: './market-report-publish.component.html',
  styleUrls: ['./market-report-publish.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, RmaIconComponent, MatMenuModule, AsyncPipe, TranslocoPipe],
  providers: [RmaModalService],
})
export class MarketReportPublishComponent implements OnInit, OnChanges {
  private readonly profile = inject(PublicProfileContextService);
  private readonly dialogService = inject(MatDialog);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly marketReportApiService = inject(MarketReportApiService);
  private readonly modalService = inject(RmaModalService);
  private readonly qrCodeService = inject(QrCodeService);
  private readonly rmaBaseUrl = inject(appendToRmaBaseUrl);
  private readonly snackBar = inject(MatSnackBar);

  @Input() report: MarketReport;

  public readonly isPublished$ = new BehaviorSubject(false);
  public showSmsMenuItem$: Observable<boolean>;

  public marketReportCommunicationTypeEnum = MarketReportCommunicationType;

  public manageReportsUrl: string;
  public editUrl: string;

  private qrCode$: Observable<string>;

  ngOnInit(): void {
    this.qrCode$ = this.qrCodeService
      .getQrCodeForText(this.rmaBaseUrl(`/market-report?code=${this.report.listingReportCode}&utm_medium=qr-code`))
      .pipe(shareReplay(1));

    this.showSmsMenuItem$ = this.featureFlagService.getFeature$(FeatureFlags.EnableMobileNumberForMarketReport, true);
  }

  ngOnChanges(_: SimpleChanges): void {
    this.isPublished$.next(this.report.isPublished);

    this.editUrl = this.rmaBaseUrl(`/dashboard/market-reports/property-details/${this.report.listingReportId}`);
    this.manageReportsUrl = this.rmaBaseUrl('/dashboard/market-reports/manage');
  }

  public confirmPublish() {
    const dialog = this.modalService.openConfirmAction({
      title: 'marketReports.confirm-publish-title',
      message: 'marketReports.confirm-publish-message',
      buttonText: 'marketReports.actions.publish',
      useTranslation: true,
      onConfirm: () => this.publish().pipe(map(() => true)),
    });

    dialog.afterClosed().subscribe((isComplete: boolean) => {
      if (isComplete === true) {
        this.isPublished$.next(true);
        this.snackBar.open('Report was successfully published', undefined, {
          duration: 5000,
        });
      }
    });
  }

  public downloadPdf() {
    this.marketReportApiService.getPdf(this.report.listingReportId).subscribe({
      next: (response) => window.open(response, '_blank'),
      error: (response) =>
        this.snackBar.open(response.error.Message, undefined, {
          duration: 5000,
        }),
    });
  }

  public downloadQrCode() {
    this.qrCode$.pipe(untilDestroyed(this)).subscribe((qrCodeDataUri) => {
      this.dialogService.open(
        QrCodeDownloadComponent,
        applyWithDialogDefaults({
          data: {
            dataUri: qrCodeDataUri,
          },
        }),
      );
    });
  }

  public openSendCommsDialog(commType: MarketReportCommunicationType): void {
    const ref = this.dialogService.open(
      MarketReportCommunicationFormComponent,
      applyWithDialogDefaults({
        data: {
          type: commType,
          marketReport: this.report,
        },
      }),
    );

    ref
      .afterClosed()
      .pipe(filter((result) => result === true))
      .subscribe(() => this.snackBar.open('Message sent', undefined, { duration: 5000 }));
  }

  private publish(): Observable<void> {
    return this.profile.profileIdentifier$.pipe(
      switchMap(({ profileType, profileCode }) =>
        this.marketReportApiService.publishReport(
          this.report.listingReportId,
          this.report.propertyDetails.transactionType,
          profileType,
          profileCode,
        ),
      ),
    );
  }
}
